/**************************************************************************************************
 *
 *       @file ./auth-landing-root-view.tsx
 *
 *       Presentational component. Handles:
 *           1) Background (colour)
 *           2) Top-level page layout, consisting of:
 *              - 2 panels splitting the page from left-to-right
 *              - footer across bottom of page (sticky)
 *           3) Gray divider between each panel
 *
 *       Receives each sub-component of the page as children
 *
 */

/******************************************** STYLING *********************************************/
const s = require('./auth-landing-root.scss');

/************************************** THIRD-PARTY IMPORTS ***************************************/
import React from 'react';
import cn from 'classnames';

/**************************************** TYPE DEFINITIONS ****************************************/
interface AuthLandingRootViewProps {
    infoPanel: JSX.Element;
    authPanel: JSX.Element;
    footer: JSX.Element;
}

/******************************************** HELPERS *********************************************/
/**
 * Line down center of prelogin page
 */
const CenterDivider = () => <div className={cn(s[`auth-landing-center-divider`])} />;

CenterDivider.displayName = `CenterDivider`;

/********************************************* EXPORT *********************************************/
/**
 * Wrapper for prelogin pages, comprised of:
 *     Background
 *     Left panel    [infoPane]
 *     Right "card"  [authPanel]
 *     Footer        [footer]
 *
 * Marks the 'main' area containing the primary content
 *
 * @param {JSX.Element} infoPanel Download CANImmunize mobile links section
 * @param {JSX.Element} authPanel Main card; prop contains current card content:
 *                               login, signup, etc (based on current route)
 * @param {JSX.Element} footer Containing language changer & links
 */
export const AuthLandingRootView = (props: AuthLandingRootViewProps) => {
    return (
        <div className={cn(s[`prelogin-bg-fill`], `w-100p h-100p`, `ovx-hidden`)}>
            <div className={cn(s[`auth-landing-outer-layout-wrapper`])}>
                {/* LEFT PANEL */}
                <div className={cn(`flex align-self-center`, s[`info-panel`], `ml-40`)}>
                    {props.infoPanel}
                </div>

                {/* DIVIDER BETWEEN PANELS */}
                <CenterDivider />

                {/* RIGHT PANEL */}
                {/* Main prelogin content (login, signup, etc.) */}
                <div className={cn(`flex align-self-center`)}>
                    {/* Leave <main> for accessibility */}
                    <main role="main">{props.authPanel}</main>
                </div>
            </div>

            {/* FOOTER */}
            {props.footer}
        </div>
    );
};

AuthLandingRootView.displayName = `AuthLandingRootView`;
